import { interpolateRgb } from 'd3-interpolate'
import { scaleLinear } from 'd3-scale'
import { useMemo } from 'react'

import type { AreaGraphElement, FillFunction, OverlayLine } from '@/features/bidding/components/cascading/charts/types'
import { getMarketColor } from '@/features/bidding/constants/marketColors'
import type { AggregateAvailabilityResponse } from '@/features/bidding/types/aggregateAvailability'
import type { AggregateSuggestedBidResponse } from '@/features/bidding/types/aggregateSuggestedBid'
import type { BidLevelInput } from '@/features/bidding/types/bidLadderInput'
import type { BidLevelInputSymmetrical } from '@/features/bidding/types/bidLadderInputSymmetrical'
import type { ForecastMarketPair } from '@/features/bidding/types/forecastPricing'
import {
  buildAggregateHourlyMap,
  getPtuHour,
  isHourOverAllocated,
  resolveVolumeCapacityForHour,
} from '@/features/bidding/utils/cascading/bidLadder/resolveVolumeCapacity'
import { isSymmetricMarket } from '@/features/bidding/utils/forecast/marketProgramNames'
import { getMarketVolumeGranularity } from '@/features/bidding/utils/marketCapabilities'

const HOURS_PER_DAY = 24

export type LevelVolume = {
  levelId: string
  levelIndex: number
  upMW: number
  downMW: number
}

type UseHourlyVolumeDataParams = {
  levels: BidLevelInput[] | BidLevelInputSymmetrical[]
  market: ForecastMarketPair
  /** Base date for creating datetime objects (uses hour 0-23) */
  baseDate: Date
  /** Aggregate suggested bids to show as overlay lines on the chart */
  aggregateSuggestedBids?: AggregateSuggestedBidResponse | null
  /** Aggregate availability to show as overlay lines on the chart */
  aggregateAvailability?: AggregateAvailabilityResponse | null
}

type UseHourlyVolumeDataResult = {
  data: AreaGraphElement[]
  datetimes: Date[]
  layerKeys: string[]
  layerFill: FillFunction
  /** Actual total volume (sum of UP or DOWN, whichever is larger). Returns 0 if no volume assigned. */
  totalVolume: number
  /** Overlay lines for aggregate suggested bids and availability */
  overlayLines: OverlayLine[]
}

/**
 * Transforms bid level inputs into AreaGraph-compatible format for volume visualization.
 *
 * - UP direction volumes are positive (stack above zero)
 * - DOWN direction volumes are negative (stack below zero)
 * - For symmetric markets, DOWN mirrors UP values
 *
 * Supports both directional (BidLevelInput) and symmetrical (BidLevelInputSymmetrical) inputs.
 */
const SUGGESTED_LINE_COLOR = '#1e293b'
const AVAILABILITY_LINE_COLOR = '#0284c7'
const WATTS_TO_MW = 1_000_000

export function useHourlyVolumeData({
  levels,
  market,
  baseDate,
  aggregateSuggestedBids,
  aggregateAvailability,
}: UseHourlyVolumeDataParams): UseHourlyVolumeDataResult {
  return useMemo(() => {
    // Use market type to determine if symmetrical - more reliable than checking data structure
    const isSymmetric = isSymmetricMarket(market)
    const baseColor = getMarketColor(market)
    const granularityMW = getMarketVolumeGranularity(market).stepMW

    // Pre-build aggregate lookup maps for volume capacity resolution
    // For symmetric markets, use 'up' direction (programs without _DOWN suffix) for both directions
    const suggestedUpMap = buildAggregateHourlyMap(aggregateSuggestedBids, 'up')
    const suggestedDownMap = isSymmetric ? suggestedUpMap : buildAggregateHourlyMap(aggregateSuggestedBids, 'down')
    const availableUpMap = buildAggregateHourlyMap(aggregateAvailability, 'up')
    const availableDownMap = isSymmetric ? availableUpMap : buildAggregateHourlyMap(aggregateAvailability, 'down')

    // Build layer keys: one for each level's UP and DOWN
    // Order: all UP layers first (stacked above 0), then all DOWN layers (stacked below 0)
    const upLayerKeys = levels.map((_, i) => `up_level_${i}`)
    const downLayerKeys = levels.map((_, i) => `down_level_${i}`)
    const layerKeys = [...upLayerKeys, ...downLayerKeys]

    // Create data points for each hour (volumes may vary per hour with VolumeCapacity)
    const data: AreaGraphElement[] = []
    const datetimes: Date[] = []
    let maxTotalUp = 0
    let maxTotalDown = 0

    for (let hour = 0; hour < HOURS_PER_DAY; hour++) {
      const datetime = new Date(baseDate)
      datetime.setHours(hour, 0, 0, 0)
      datetimes.push(datetime)

      // Calculate per-level volumes for this specific hour
      const levelVolumes: LevelVolume[] = levels.map((level, levelIndex) => {
        let upMW = 0
        let downMW = 0

        if (isSymmetric) {
          const symLevel = level as BidLevelInputSymmetrical
          const groupMW = symLevel.assignedGroups.reduce((sum, group) => sum + group.capacityMW, 0)
          if (groupMW > 0) {
            upMW = groupMW
            downMW = groupMW
          } else {
            const resolved = resolveVolumeCapacityForHour(
              symLevel.volumeCapacity,
              hour,
              suggestedUpMap,
              availableUpMap,
              granularityMW,
            )
            upMW = resolved
            downMW = resolved
          }
        } else {
          const directionalLevel = level as BidLevelInput
          const upFromGroups = directionalLevel.assignedGroups
            .filter((g) => g.direction === 'up')
            .reduce((sum, g) => sum + g.capacityMW, 0)
          const downFromGroups = directionalLevel.assignedGroups
            .filter((g) => g.direction === 'down')
            .reduce((sum, g) => sum + g.capacityMW, 0)

          upMW =
            upFromGroups > 0
              ? upFromGroups
              : resolveVolumeCapacityForHour(
                  directionalLevel.upVolumeCapacity,
                  hour,
                  suggestedUpMap,
                  availableUpMap,
                  granularityMW,
                )
          downMW =
            downFromGroups > 0
              ? downFromGroups
              : resolveVolumeCapacityForHour(
                  directionalLevel.downVolumeCapacity,
                  hour,
                  suggestedDownMap,
                  availableDownMap,
                  granularityMW,
                )
        }

        return { levelId: level.id, levelIndex, upMW, downMW }
      })

      // Build layerData for this hour
      const layerData: Record<string, number> = {}
      let hourTotalUp = 0
      let hourTotalDown = 0

      for (let i = 0; i < levelVolumes.length; i++) {
        const vol = levelVolumes[i]
        layerData[`up_level_${i}`] = vol.upMW
        layerData[`down_level_${i}`] = -vol.downMW // Negative for below-zero stacking
        hourTotalUp += vol.upMW
        hourTotalDown += vol.downMW
      }

      maxTotalUp = Math.max(maxTotalUp, hourTotalUp)
      maxTotalDown = Math.max(maxTotalDown, hourTotalDown)

      // Check if allocated volume exceeds available volume for this hour
      const overAllocated = isHourOverAllocated(
        hourTotalUp,
        hourTotalDown,
        availableUpMap.get(hour) ?? 0,
        availableDownMap.get(hour) ?? 0,
      )

      data.push({
        datetime,
        meta: { hour, levelVolumes, overAllocated },
        layerData,
      })
    }

    // Total volume is the max across all hours (for hasData check and color scaling)
    const totalVolume = Math.max(maxTotalUp, maxTotalDown)

    // For color scaling, use minimum of 1 to avoid division by zero
    const maxVolumeForScaling = Math.max(totalVolume, 1)

    // Create color interpolator for gradient effect
    // Lighter color for lower values, base color for higher values
    const lightColor = interpolateRgb('#ffffff', baseColor)(0.2)
    const interpolator = interpolateRgb(lightColor, baseColor)

    // Value scale for color intensity (based on cumulative position in stack)
    const colorScale = scaleLinear().domain([0, maxVolumeForScaling]).range([0.3, 1]).clamp(true)

    // Layer fill function - same gradient treatment for all levels
    const layerFill: FillFunction = (key) => (d) => {
      const value = Math.abs(d.data.layerData[key] ?? 0)
      const t = colorScale(value)
      return interpolator(t)
    }

    // Build overlay lines from aggregate suggested bids and availability
    const overlayLines: OverlayLine[] = []

    if (aggregateSuggestedBids?.suggestions) {
      for (const suggestion of aggregateSuggestedBids.suggestions) {
        const programName = suggestion.marketProgram as string
        const isDown = programName.includes('_DOWN')

        // For symmetric markets, skip _DOWN entries — the non-DOWN entry covers both directions
        if (isSymmetric && isDown) continue

        const layerKey = `suggested_${isDown ? 'down' : 'up'}`

        // Store suggested volumes into layerData for each hour
        for (const ptuEntry of suggestion.ptus) {
          const hour = getPtuHour(ptuEntry.ptu.start)
          const volumeMW = ptuEntry.volume.quantity / WATTS_TO_MW
          const dataPoint = data[hour]
          if (dataPoint) {
            dataPoint.layerData[layerKey] = volumeMW
            if (isSymmetric) {
              dataPoint.layerData['suggested_down'] = -volumeMW
            } else if (isDown) {
              dataPoint.layerData[layerKey] = -volumeMW
            }
          }
        }

        overlayLines.push({
          key: layerKey,
          getValue: (d: AreaGraphElement) => d.layerData[layerKey] ?? 0,
          color: SUGGESTED_LINE_COLOR,
          strokeWidth: 1.5,
          opacity: 0.8,
          strokeDasharray: '6 3',
        })

        // For symmetric markets, add mirror overlay line for the down side
        if (isSymmetric) {
          overlayLines.push({
            key: 'suggested_down',
            getValue: (d: AreaGraphElement) => d.layerData['suggested_down'] ?? 0,
            color: SUGGESTED_LINE_COLOR,
            strokeWidth: 1.5,
            opacity: 0.8,
            strokeDasharray: '6 3',
          })
        }
      }
    }

    if (aggregateAvailability?.availabilities) {
      for (const availability of aggregateAvailability.availabilities) {
        const programName = availability.marketProgram as string
        const isDown = programName.includes('_DOWN')

        // For symmetric markets, skip _DOWN entries — the non-DOWN entry covers both directions
        if (isSymmetric && isDown) continue

        const layerKey = `available_${isDown ? 'down' : 'up'}`

        for (const ptuEntry of availability.ptus) {
          const hour = getPtuHour(ptuEntry.ptu.start)
          const volumeMW = ptuEntry.volume.quantity / WATTS_TO_MW
          const dataPoint = data[hour]
          if (dataPoint) {
            dataPoint.layerData[layerKey] = volumeMW
            if (isSymmetric) {
              dataPoint.layerData['available_down'] = -volumeMW
            } else if (isDown) {
              dataPoint.layerData[layerKey] = -volumeMW
            }
          }
        }

        overlayLines.push({
          key: layerKey,
          getValue: (d: AreaGraphElement) => d.layerData[layerKey] ?? 0,
          color: AVAILABILITY_LINE_COLOR,
          strokeWidth: 1.5,
          opacity: 0.8,
          strokeDasharray: '6 3',
        })

        // For symmetric markets, add mirror overlay line for the down side
        if (isSymmetric) {
          overlayLines.push({
            key: 'available_down',
            getValue: (d: AreaGraphElement) => d.layerData['available_down'] ?? 0,
            color: AVAILABILITY_LINE_COLOR,
            strokeWidth: 1.5,
            opacity: 0.8,
            strokeDasharray: '6 3',
          })
        }
      }
    }

    return { data, datetimes, layerKeys, layerFill, totalVolume, overlayLines }
  }, [levels, market, baseDate, aggregateSuggestedBids, aggregateAvailability])
}
